import React, { memo, useCallback, useMemo, useState } from 'react';
import type { Chat } from '@trinity/chat';
import { ChatMarkdown } from '@trinity/chat-components/components/Markdown/Markdown';
import { AttachmentsContext } from '../Chat/context';
import { ChatMessageAttachments } from '../Chat/Message.Attachments';
import { EntryButtons } from './DynamicForm.introduction.buttons';
import { DraftTable, type DraftPreview } from './DynamicForm.introduction.drafts';
import {
  Message,
  VoiceWarningBox,
} from './DynamicForm.styles.index';

// Production constants
const CONSTANTS = {
  DEFAULT_PROMPT: '',
  DEFAULT_WARNING: '',
} as const;

const sanitizeText = (value: string | undefined, fallback: string): string => {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
};

const IntroResponseSection: React.FC<{
  introResponse?: { value: string; attachments?: unknown[] };
}> = ({ introResponse }) => {
  const introAttachments = useMemo(() => {
    return (introResponse?.attachments as Chat.Attachment.Item[] | undefined) || [];
  }, [introResponse?.attachments]);

  if (!introResponse || !introResponse.value || !introResponse.value.trim()) {
    return null;
  }

  return (
    <AttachmentsContext.Provider value={introAttachments}>
      <ChatMarkdown value={introResponse.value} />
      {introResponse.attachments && Array.isArray(introResponse.attachments) && introResponse.attachments.length > 0 && (
        <ChatMessageAttachments attachments={introResponse.attachments as Chat.Attachment.Item[]} />
      )}
    </AttachmentsContext.Provider>
  );
};

interface DynamicFormIntroductionProps {
  onManualEntry: () => void;
  onSpeechEntry: () => void;
  onCancel: () => void;
  onLoadDraft?: (draftId: string) => void;
  prompt?: string;
  warningMessage?: string;
  showCancelButton?: boolean;
  showSpeechOption?: boolean;
  showManualButton?: boolean;
  showWarning?: boolean;
  isVoiceActive?: boolean;
  availableDrafts?: DraftPreview[];
  onViewDrafts?: () => void;
  draftOnlyMode?: boolean; // When true, only show draft options, hide new entry buttons
  introResponse?: { value: string; attachments?: unknown[] }; // Optional response content to display above form entry options
}

function computeVisibility(showVoiceWarning: boolean, isVoiceActive: boolean, draftOnlyMode: boolean, warningMessage: string | undefined, availableDrafts: DraftPreview[]) {
  const shouldShowButtons = !showVoiceWarning && !isVoiceActive && !draftOnlyMode;
  const shouldShowVoiceWarning = (showVoiceWarning || isVoiceActive) && !!warningMessage && warningMessage.trim().length > 0;
  const shouldShowDraftTable = draftOnlyMode && availableDrafts && availableDrafts.length > 0;
  return { shouldShowButtons, shouldShowVoiceWarning, shouldShowDraftTable };
}

function IntroContent({ validPrompt, shouldShowDraftTable, availableDrafts, onLoadDraft, shouldShowButtons, handlers, buttonProps, shouldShowVoiceWarning, warningMessage }: {
  validPrompt: string;
  shouldShowDraftTable: boolean;
  availableDrafts: DraftPreview[];
  onLoadDraft?: (draftId: string) => void;
  shouldShowButtons: boolean;
  handlers: { handleManualEntry: () => void; handleSpeechEntry: () => void; handleCancel: () => void };
  buttonProps: { showManualButton: boolean; showSpeechOption: boolean; showCancelButton: boolean };
  shouldShowVoiceWarning: boolean;
  warningMessage?: string;
}) {
  return (
    <>
      {validPrompt && (
        <Message role="status" aria-live="polite">
          {validPrompt}
        </Message>
      )}

      {shouldShowDraftTable && (
        <DraftTable
          availableDrafts={availableDrafts}
          onLoadDraft={onLoadDraft || (() => { })}
        />
      )}

      {shouldShowButtons && (
        <EntryButtons
          onManualEntry={handlers.handleManualEntry}
          onSpeechEntry={handlers.handleSpeechEntry}
          onCancel={handlers.handleCancel}
          showManualButton={buttonProps.showManualButton}
          showSpeechOption={buttonProps.showSpeechOption}
          showCancelButton={buttonProps.showCancelButton}
        />
      )}

      {shouldShowVoiceWarning && (
        <VoiceWarningBox
          role="region"
          aria-label="Voice input instructions"
        >
          {warningMessage}
        </VoiceWarningBox>
      )}
    </>
  );
}

export const DynamicFormIntroduction: React.FC<DynamicFormIntroductionProps> = memo(({
  onManualEntry,
  onSpeechEntry,
  onCancel,
  onLoadDraft,
  prompt = CONSTANTS.DEFAULT_PROMPT,
  warningMessage,
  showCancelButton = true,
  showSpeechOption = true,
  showManualButton = true,
  showWarning = true,
  isVoiceActive = false,
  availableDrafts = [],
  onViewDrafts,
  draftOnlyMode = false,
  introResponse,
}) => {
  const [showVoiceWarning, setShowVoiceWarning] = useState(false);

  const handleManualEntry = useCallback(() => {
    setShowVoiceWarning(false);
    onManualEntry?.();
  }, [onManualEntry]);

  const handleSpeechEntry = useCallback(() => {
    setShowVoiceWarning(true);
    onSpeechEntry?.();
  }, [onSpeechEntry]);

  const handleCancel = useCallback(() => {
    setShowVoiceWarning(false);
    onCancel?.();
  }, [onCancel]);

  const validPrompt = useMemo(() => sanitizeText(prompt, CONSTANTS.DEFAULT_PROMPT), [prompt]);
  const { shouldShowButtons, shouldShowVoiceWarning, shouldShowDraftTable } = computeVisibility(showVoiceWarning, isVoiceActive, draftOnlyMode, warningMessage, availableDrafts);

  return (
    <>
      <IntroResponseSection introResponse={introResponse} />

      <IntroContent
        validPrompt={validPrompt}
        shouldShowDraftTable={shouldShowDraftTable}
        availableDrafts={availableDrafts}
        onLoadDraft={onLoadDraft}
        shouldShowButtons={shouldShowButtons}
        handlers={{ handleManualEntry, handleSpeechEntry, handleCancel }}
        buttonProps={{ showManualButton, showSpeechOption, showCancelButton }}
        shouldShowVoiceWarning={shouldShowVoiceWarning}
        warningMessage={warningMessage}
      />
    </>
  );
});

DynamicFormIntroduction.displayName = 'DynamicFormIntroduction';

export default DynamicFormIntroduction;
export type { DraftPreview } from './DynamicForm.introduction.drafts';
